import type { LintDiagnostic, LintResult } from '../types/lint.js';
import type { OrgChannelPolicy } from '../types/channel.js';
import { extractFrontmatter } from '../parser/frontmatter.js';
import { validateHeadings } from '../parser/headings.js';
import { validateCharterFrontmatter, validateToolsFrontmatter } from '../schemas/validators.js';
import { runSchemaRules } from './rules/schema.js';
import { runSemanticRules } from './rules/semantic.js';
import { runChannelRules } from './rules/channel.js';
import { runCrossFileRules } from './rules/cross-file.js';
import {
  runMultiAgentRules,
  type TeamPeerInfo,
  type CrossTeamGrantSnapshot,
} from './rules/multi-agent.js';

export interface LintContext {
  orgChannelPolicy?: OrgChannelPolicy;
  /**
   * ENG-4465: roster of peer agents on the same team. When provided, lintCharter
   * cross-checks each `multi_agent.telegram_peers[]` entry against the roster.
   * Omit this field entirely when running outside a team context — passing an
   * empty array still runs the rule (and would fire UNKNOWN_PEER for every
   * declared peer). The rule no-ops only when the charter itself has no
   * `multi_agent.telegram_peers` entries.
   */
  teamPeers?: TeamPeerInfo[];
  /**
   * ENG-4938 / ENG-4929 §5.1: inbound cross_team_peer_grants (grants where
   * granted_to_team_id matches the linting team). Used to validate any
   * `multi_agent.telegram_peers[].cross_team_grant_id` references. Omit
   * entirely when running outside a team context.
   */
  crossTeamGrants?: CrossTeamGrantSnapshot[];
}

export type { TeamPeerInfo, CrossTeamGrantSnapshot } from './rules/multi-agent.js';

function buildResult(diagnostics: LintDiagnostic[]): LintResult {
  const errors = diagnostics.filter((d) => d.severity === 'error');
  const warnings = diagnostics.filter((d) => d.severity === 'warning');
  return { ok: errors.length === 0, errors, warnings };
}

export function lintCharter(content: string, ctx: LintContext = {}): LintResult {
  const diagnostics: LintDiagnostic[] = [];
  const { frontmatter, body, error } = extractFrontmatter(content);

  if (error || !frontmatter) {
    diagnostics.push({
      file: 'CHARTER.md',
      code: 'CHARTER.PARSE.FRONTMATTER',
      severity: 'error',
      message: error ?? 'Failed to parse frontmatter',
    });
    return buildResult(diagnostics);
  }

  // Schema validation
  const schemaResult = validateCharterFrontmatter(frontmatter);
  diagnostics.push(...runSchemaRules('CHARTER.md', schemaResult));

  // Heading validation
  const missingHeadings = validateHeadings(body);
  for (const heading of missingHeadings) {
    diagnostics.push({
      file: 'CHARTER.md',
      code: 'CHARTER.HEADING.MISSING',
      path: heading,
      severity: 'error',
      message: `Required heading "## ${heading}" is missing`,
    });
  }

  if (schemaResult.valid && schemaResult.data) {
    diagnostics.push(...runSemanticRules('CHARTER.md', schemaResult.data));
    diagnostics.push(...runChannelRules(schemaResult.data, ctx.orgChannelPolicy));
    // CodeRabbit (post-merge of #865): also run when only crossTeamGrants
    // is supplied. The previous guard meant a caller that loaded an
    // inbound-grants snapshot but no roster (e.g. CHARTER-only lint from
    // the webapp) would silently skip GRANT_INVALID / GRANT_GRANDFATHERED
    // diagnostics. Default teamPeers to [] in that path — runMultiAgentRules
    // handles an empty roster correctly (same-team checks just no-op).
    if (ctx.teamPeers !== undefined || ctx.crossTeamGrants !== undefined) {
      diagnostics.push(
        ...runMultiAgentRules(schemaResult.data, ctx.teamPeers ?? [], {
          crossTeamGrants: ctx.crossTeamGrants,
        }),
      );
    }
  }

  return buildResult(diagnostics);
}

export function lintTools(content: string): LintResult {
  const diagnostics: LintDiagnostic[] = [];
  const { frontmatter, error } = extractFrontmatter(content);

  if (error || !frontmatter) {
    diagnostics.push({
      file: 'TOOLS.md',
      code: 'TOOLS.PARSE.FRONTMATTER',
      severity: 'error',
      message: error ?? 'Failed to parse frontmatter',
    });
    return buildResult(diagnostics);
  }

  const schemaResult = validateToolsFrontmatter(frontmatter);
  diagnostics.push(...runSchemaRules('TOOLS.md', schemaResult));

  if (schemaResult.valid && schemaResult.data) {
    // Check HTTP tools require network allowlist
    for (let i = 0; i < schemaResult.data.tools.length; i++) {
      const tool = schemaResult.data.tools[i]!;
      if (tool.type === 'http' && (!tool.network?.allowlist_domains || tool.network.allowlist_domains.length === 0)) {
        diagnostics.push({
          file: 'TOOLS.md',
          code: 'TOOLS.NETWORK.ALLOWLIST_REQUIRED',
          path: `tools[${i}].network.allowlist_domains`,
          severity: 'error',
          message: `HTTP tool "${tool.id}" requires at least one allowlist_domains entry`,
        });
      }
    }

    // Check for inline secrets
    for (let i = 0; i < schemaResult.data.tools.length; i++) {
      const tool = schemaResult.data.tools[i]!;
      for (const [key, value] of Object.entries(tool.auth.secrets)) {
        if (value && !value.startsWith('secret_ref://')) {
          diagnostics.push({
            file: 'TOOLS.md',
            code: 'TOOLS.SECRETS.INLINE',
            path: `tools[${i}].auth.secrets.${key}`,
            severity: 'error',
            message: `Secret "${key}" in tool "${tool.id}" must use secret_ref:// reference, not inline value`,
          });
        }
      }
    }

    // Prod safety: warn if default_network_policy is allow
    if (schemaResult.data.environment === 'prod' && schemaResult.data.global_controls.default_network_policy === 'allow') {
      diagnostics.push({
        file: 'TOOLS.md',
        code: 'TOOLS.PROD.NETWORK_ALLOW',
        path: 'global_controls.default_network_policy',
        severity: 'warning',
        message: 'Production agents should use deny-by-default network policy',
      });
    }
  }

  return buildResult(diagnostics);
}

export function lintCrossFile(charterContent: string, toolsContent: string): LintResult {
  const diagnostics: LintDiagnostic[] = [];

  const charterParsed = extractFrontmatter(charterContent);
  const toolsParsed = extractFrontmatter(toolsContent);

  if (!charterParsed.frontmatter || !toolsParsed.frontmatter) {
    return buildResult(diagnostics);
  }

  const charterValidation = validateCharterFrontmatter(charterParsed.frontmatter);
  const toolsValidation = validateToolsFrontmatter(toolsParsed.frontmatter);

  if (charterValidation.valid && toolsValidation.valid && charterValidation.data && toolsValidation.data) {
    diagnostics.push(...runCrossFileRules(charterValidation.data, toolsValidation.data));
  }

  return buildResult(diagnostics);
}

export function lintAll(
  charterContent: string,
  toolsContent: string,
  ctx: LintContext = {},
): LintResult {
  const charterResult = lintCharter(charterContent, ctx);
  const toolsResult = lintTools(toolsContent);
  const crossResult = lintCrossFile(charterContent, toolsContent);

  const allErrors = [...charterResult.errors, ...toolsResult.errors, ...crossResult.errors];
  const allWarnings = [...charterResult.warnings, ...toolsResult.warnings, ...crossResult.warnings];

  return {
    ok: allErrors.length === 0,
    errors: allErrors,
    warnings: allWarnings,
  };
}
